export const LEADS_LIST_CONTEXT_STATE_KEY = "leadsListContext"
const LEADS_LIST_NAVIGATION_STORAGE_KEY = "leadsListNavigationState"

function toNumber(value, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function toIdString(id) {
  return id == null ? "" : String(id)
}

function normalizeLeadSource(leadSource) {
  return leadSource === "uploaded" ? "uploaded" : "mls"
}

function leadsListUrl(leadSource) {
  return leadSource === "uploaded"
    ? "/crm/api/uploaded-leads/"
    : "/crm/api/agents/"
}

export function buildLeadDetailsPath(leadSource, id) {
  return `/leads/${leadSource}/${id}`
}

export function buildLeadsListPath(leadSource) {
  return leadSource === "uploaded" ? "/leads/?tab=uploaded" : "/leads/?tab=mls"
}

export function normalizeLeadsListContext(listContext) {
  if (!listContext) {
    return null
  }

  return {
    leadSource: normalizeLeadSource(listContext.leadSource),
    pageIndex: toNumber(listContext.pageIndex, 1),
    pageSize: toNumber(listContext.pageSize, 20),
    rowIndex: toNumber(listContext.rowIndex, 0),
    totalCount: toNumber(listContext.totalCount, 0),
    pageLeadIds: (listContext.pageLeadIds || [])
      .map(toIdString)
      .filter(Boolean),
    filters: listContext.filters || [],
    ordering: listContext.ordering || "",
  }
}

export function persistLeadsListNavigationState(listContext) {
  const normalized = normalizeLeadsListContext(listContext)
  if (!normalized) {
    return
  }
  sessionStorage.setItem(
    LEADS_LIST_NAVIGATION_STORAGE_KEY,
    JSON.stringify({ listContext: normalized })
  )
}

export function getPersistedLeadsListNavigationState() {
  const raw = sessionStorage.getItem(LEADS_LIST_NAVIGATION_STORAGE_KEY)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function getLeadsNavigationState(location, leadSource) {
  const locationState = location?.state
  const fromLocation = locationState?.[LEADS_LIST_CONTEXT_STATE_KEY]
  const raw =
    fromLocation || getPersistedLeadsListNavigationState()?.listContext
  const listContext = normalizeLeadsListContext(raw)
  if (!listContext) {
    return { listContext: null }
  }
  if (
    leadSource &&
    listContext.leadSource !== normalizeLeadSource(leadSource)
  ) {
    return { listContext: null }
  }
  return { listContext }
}

export function alignLeadsListContextToLeadId(listContext, leadId) {
  if (!listContext || leadId == null || leadId === "") {
    return listContext
  }
  const idx = listContext.pageLeadIds.findIndex(
    (id) => id === toIdString(leadId)
  )
  if (idx < 0) {
    return null
  }
  if (idx === listContext.rowIndex) {
    return listContext
  }
  return { ...listContext, rowIndex: idx }
}

export function buildLeadsListContext({
  leadSource,
  filters,
  ordering,
  pageIndex,
  pageSize,
  rowIndex,
  pageLeadIds,
  totalCount,
}) {
  return normalizeLeadsListContext({
    leadSource,
    filters,
    ordering,
    pageIndex,
    pageSize,
    rowIndex,
    pageLeadIds,
    totalCount,
  })
}

export function getLeadsListGlobalIndex(listContext) {
  if (!listContext) {
    return null
  }
  return (
    (listContext.pageIndex - 1) * listContext.pageSize +
    listContext.rowIndex +
    1
  )
}

export function canGoToPreviousLead(listContext) {
  if (!listContext || listContext.rowIndex < 0) {
    return false
  }
  const globalIndex = getLeadsListGlobalIndex(listContext)
  return globalIndex > 1
}

export function canGoToNextLead(listContext) {
  if (!listContext || listContext.rowIndex < 0) {
    return false
  }
  const globalIndex = getLeadsListGlobalIndex(listContext)
  return globalIndex < listContext.totalCount
}

export function navigateToLeadDetails(
  navigate,
  leadSource,
  leadId,
  listContext
) {
  const normalized = normalizeLeadsListContext(listContext)
  if (normalized) {
    persistLeadsListNavigationState(normalized)
  }
  navigate(buildLeadDetailsPath(leadSource, leadId), {
    state: normalized
      ? { [LEADS_LIST_CONTEXT_STATE_KEY]: normalized }
      : undefined,
  })
}

async function fetchLeadsPage(axios, listContext, pageIndex) {
  return axios.post(
    leadsListUrl(listContext.leadSource),
    { filters: listContext.filters },
    {
      params: {
        q: null,
        page: pageIndex,
        page_size: listContext.pageSize,
        ordering: listContext.ordering,
      },
    }
  )
}

export async function resolveAdjacentLead(axios, listContext, direction) {
  const normalizedContext = normalizeLeadsListContext(listContext)
  const { rowIndex, pageLeadIds } = normalizedContext

  if (rowIndex < 0 || !pageLeadIds.length) {
    return null
  }

  if (direction === "next") {
    if (rowIndex < pageLeadIds.length - 1) {
      const newRowIndex = rowIndex + 1
      return {
        leadId: pageLeadIds[newRowIndex],
        listContext: {
          ...normalizedContext,
          rowIndex: newRowIndex,
        },
      }
    }

    const globalIndex = getLeadsListGlobalIndex(normalizedContext)
    if (globalIndex >= normalizedContext.totalCount) {
      return null
    }

    const nextPageIndex = normalizedContext.pageIndex + 1
    const { data } = await fetchLeadsPage(
      axios,
      normalizedContext,
      nextPageIndex
    )
    if (!data.results?.length) {
      return null
    }

    return {
      leadId: toIdString(data.results[0].id),
      listContext: {
        ...normalizedContext,
        pageIndex: nextPageIndex,
        rowIndex: 0,
        pageLeadIds: data.results.map((result) => toIdString(result.id)),
        totalCount: toNumber(data.count, normalizedContext.totalCount),
      },
    }
  }

  if (direction === "prev") {
    if (rowIndex > 0) {
      const newRowIndex = rowIndex - 1
      return {
        leadId: pageLeadIds[newRowIndex],
        listContext: {
          ...normalizedContext,
          rowIndex: newRowIndex,
        },
      }
    }

    if (normalizedContext.pageIndex <= 1) {
      return null
    }

    const prevPageIndex = normalizedContext.pageIndex - 1
    const { data } = await fetchLeadsPage(
      axios,
      normalizedContext,
      prevPageIndex
    )
    if (!data.results?.length) {
      return null
    }

    const lastIndex = data.results.length - 1
    return {
      leadId: toIdString(data.results[lastIndex].id),
      listContext: {
        ...normalizedContext,
        pageIndex: prevPageIndex,
        rowIndex: lastIndex,
        pageLeadIds: data.results.map((result) => toIdString(result.id)),
        totalCount: toNumber(data.count, normalizedContext.totalCount),
      },
    }
  }

  return null
}
